import React, { useState, useContext, useRef, useEffect, useCallback } from 'react';
import './Chat.css';
import {
  FaHeart, FaRegHeart, FaRegComment, FaPaperPlane,
  FaUsers, FaHashtag, FaBullhorn, FaQuestionCircle,
  FaHandshake, FaStar, FaTrophy, FaBriefcase, FaTrash,
  FaChevronUp, FaThumbtack,
} from 'react-icons/fa';
import { UserContext } from '../../App';
import { CanalApi } from '../../api/CanalApi';

const BASE_API = (process.env.REACT_APP_APIURL || 'http://localhost:4000').replace(/\/$/, '');

const ICON_MAP = {
  FaHashtag:        <FaHashtag />,
  FaQuestionCircle: <FaQuestionCircle />,
  FaBullhorn:       <FaBullhorn />,
  FaUsers:          <FaUsers />,
  FaHandshake:      <FaHandshake />,
  FaStar:           <FaStar />,
  FaTrophy:         <FaTrophy />,
  FaBriefcase:      <FaBriefcase />,
};

const AVATAR_COLORS = ['#c9960c','#a87c0a','#10b981','#d4a843','#ef4444','#f0c040','#b8860b'];
const colorFor = (name) => AVATAR_COLORS[(name || 'U').charCodeAt(0) % AVATAR_COLORS.length];

const timeSince = (ts) => {
  const diff = (Date.now() - new Date(ts)) / 1000;
  if (diff < 60)    return 'ahora';
  if (diff < 3600)  return `hace ${Math.floor(diff / 60)}min`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)}h`;
  return new Date(ts).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' });
};

const avatarSrc = (url) =>
  url ? (url.startsWith('/avatars') ? `${BASE_API}${url}` : url) : null;

const Avatar = ({ username, avatarUrl, size = 'md' }) => {
  const src  = avatarSrc(avatarUrl);
  const cls  = size === 'sm' ? 'chat-avatar chat-avatar--sm' : 'chat-avatar';
  return (
    <div className={cls} style={{ background: src ? 'transparent' : colorFor(username) }}>
      {src
        ? <img src={src} alt={username} className="chat-avatar-img" />
        : (username || 'U').slice(0, 2).toUpperCase()
      }
    </div>
  );
};

const REPLIES_PAGE = 30;
const MSGS_PAGE    = 20;

const canalApi = new CanalApi();

const Chat = () => {
  const user = useContext(UserContext);
  const [canales, setCanales]           = useState([]);
  const [activeRoom, setActiveRoom]     = useState(null);
  const [posts, setPosts]               = useState([]);
  const [msgOffset, setMsgOffset]       = useState(0);
  const [hasMoreMsgs, setHasMoreMsgs]   = useState(false);
  const [loadingMore, setLoadingMore]   = useState(false);
  const [inputValue, setInputValue]     = useState('');
  const [loading, setLoading]           = useState(true);
  const [openComments, setOpenComments] = useState({});
  const [replies, setReplies]           = useState({});
  const [repliesOffset, setRepliesOffset]   = useState({});
  const [repliesHasMore, setRepliesHasMore] = useState({});
  const [replyInputs, setReplyInputs]   = useState({});
  const [pinnedMsg, setPinnedMsg]       = useState(null);
  const feedRef    = useRef(null);
  const textareaRef = useRef(null);

  const canAccessCanal = useCallback((canal) => {
    if (canal.tipo === 'public') return true;
    const roles = user?.roles || [];
    if (user?.firstRole) return true;
    return roles.includes(canal.grupo_keycloak);
  }, [user?.roles, user?.firstRole]);

  // Load channels
  useEffect(() => {
    canalApi.getAllCanales()
      .then(all => {
        const visible = all.filter(canal => canal.nombre === 'Empresas' && canAccessCanal(canal));
        setCanales(visible);
        if (visible.length > 0) setActiveRoom(visible[0]);
      })
      .catch(() => {
        setCanales([]);
      })
      .finally(() => setLoading(false));
  }, [canAccessCanal]);

  // Auto-expand textarea
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 180) + 'px';
  }, [inputValue]);

  // Load messages when room changes
  useEffect(() => {
    if (!activeRoom) return;
    setPosts([]);
    setMsgOffset(0);
    setHasMoreMsgs(false);
    setPinnedMsg(null);
    canalApi.getPinnedMensaje(activeRoom.id).then(setPinnedMsg).catch(() => {});
    canalApi.getMensajes(activeRoom.id, user?.id || '', 0, MSGS_PAGE)
      .then(msgs => {
        // Backend returns DESC (newest first) → reverse for display (oldest at top)
        setPosts([...msgs].reverse());
        setMsgOffset(MSGS_PAGE);
        setHasMoreMsgs(msgs.length === MSGS_PAGE);
      })
      .catch(() => setPosts([]));
  }, [activeRoom, user?.id]);

  const loadMoreMessages = async () => {
    if (!activeRoom || loadingMore) return;
    setLoadingMore(true);
    try {
      const older = await canalApi.getMensajes(activeRoom.id, user?.id || '', msgOffset, MSGS_PAGE);
      setPosts(prev => [...older.reverse(), ...prev]);
      setMsgOffset(prev => prev + MSGS_PAGE);
      setHasMoreMsgs(older.length === MSGS_PAGE);
    } catch {}
    finally { setLoadingMore(false); }
  };

  const handleLike = async (postId, liked) => {
    if (!user?.id) return;
    try {
      if (liked) await canalApi.unlikeMensaje(postId, user.id);
      else       await canalApi.likeMensaje(postId, user.id);
      setPosts(prev => prev.map(p =>
        p.id === postId
          ? { ...p, liked: !liked, likes: liked ? parseInt(p.likes) - 1 : parseInt(p.likes) + 1 }
          : p
      ));
    } catch (err) { console.error('Like error:', err); }
  };

  const handleDelete = async (postId) => {
    if (!user?.id || !window.confirm('¿Eliminar este mensaje?')) return;
    try {
      await canalApi.deleteMensaje(postId, user.id);
      setPosts(prev => prev.filter(p => p.id !== postId));
    } catch (err) { console.error('Delete error:', err); }
  };

  const handleDeleteReply = async (postId, replyId) => {
    if (!user?.id || !window.confirm('¿Eliminar este comentario?')) return;
    try {
      await canalApi.deleteMensaje(replyId, user.id);
      setReplies(prev => ({ ...prev, [postId]: (prev[postId] || []).filter(r => r.id !== replyId) }));
      setPosts(prev => prev.map(p =>
        p.id === postId ? { ...p, comments: Math.max(0, (parseInt(p.comments) || 0) - 1) } : p
      ));
    } catch (err) { console.error('Delete reply error:', err); }
  };

  const handlePin = async (post) => {
    if (!user?.firstRole) return;
    const newPinned = pinnedMsg?.id !== post.id;
    try {
      if (pinnedMsg && pinnedMsg.id !== post.id) {
        await canalApi.pinMensaje(pinnedMsg.id, false);
      }
      await canalApi.pinMensaje(post.id, newPinned);
      setPinnedMsg(newPinned ? { ...post } : null);
    } catch (err) { console.error('Pin error:', err); }
  };

  const handleSend = async () => {
    const text = inputValue.trim();
    if (!text || !activeRoom || !user?.id) return;
    const body = text.replace(/\n/g, '<br>');
    const optimistic = {
      id: Date.now(), id_canal: activeRoom.id, id_usuario: user.id,
      username: user.username || 'Tú', avatar_url: user.avatarUrl || '',
      user_tag: user.userTag || '',
      body, created_at: new Date().toISOString(), likes: 0, liked: false, comments: 0,
    };
    setPosts(prev => [...prev, optimistic]);
    setInputValue('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
    setTimeout(() => feedRef.current?.scrollTo({ top: feedRef.current.scrollHeight, behavior: 'smooth' }), 50);
    try {
      const saved = await canalApi.sendMensaje(activeRoom.id, user.id, body);
      setPosts(prev => prev.map(p =>
        p.id === optimistic.id
          ? { ...saved, username: user.username, avatar_url: user.avatarUrl || '', user_tag: user.userTag || '', likes: 0, liked: false }
          : p
      ));
    } catch (err) { console.error('Send error:', err); }
  };

  const handleRoomClick = (canal) => {
    setActiveRoom(canal);
    setPosts([]);
    setOpenComments({});
    setReplies({});
    setRepliesOffset({});
    setRepliesHasMore({});
    setReplyInputs({});
  };

  const toggleComments = async (postId) => {
    if (openComments[postId]) { setOpenComments(prev => ({ ...prev, [postId]: false })); return; }
    setOpenComments(prev => ({ ...prev, [postId]: true }));
    if (!replies[postId]) {
      try {
        const data = await canalApi.getReplies(activeRoom.id, postId, user?.id || '', 0, REPLIES_PAGE);
        setReplies(prev => ({ ...prev, [postId]: data }));
        setRepliesOffset(prev => ({ ...prev, [postId]: REPLIES_PAGE }));
        setRepliesHasMore(prev => ({ ...prev, [postId]: data.length === REPLIES_PAGE }));
      } catch { setReplies(prev => ({ ...prev, [postId]: [] })); }
    }
  };

  const loadMoreReplies = async (postId) => {
    const offset = repliesOffset[postId] || 0;
    try {
      const data = await canalApi.getReplies(activeRoom.id, postId, user?.id || '', offset, REPLIES_PAGE);
      setReplies(prev => ({ ...prev, [postId]: [...(prev[postId] || []), ...data] }));
      setRepliesOffset(prev => ({ ...prev, [postId]: offset + REPLIES_PAGE }));
      setRepliesHasMore(prev => ({ ...prev, [postId]: data.length === REPLIES_PAGE }));
    } catch (err) { console.error('Load more replies error:', err); }
  };

  const sendReply = async (postId) => {
    const text = (replyInputs[postId] || '').trim();
    if (!text || !user?.id) return;
    setReplyInputs(prev => ({ ...prev, [postId]: '' }));
    try {
      const saved = await canalApi.sendMensaje(activeRoom.id, user.id, text, postId);
      setReplies(prev => ({
        ...prev,
        [postId]: [...(prev[postId] || []), { ...saved, username: user.username, avatar_url: user.avatarUrl || '', user_tag: user.userTag || '', likes: 0, liked: false }],
      }));
      setPosts(prev => prev.map(p =>
        p.id === postId ? { ...p, comments: (parseInt(p.comments) || 0) + 1 } : p
      ));
    } catch (err) { console.error('Reply error:', err); }
  };

  if (loading) {
    return <div className="chat-page chat-loading"><div className="chat-spinner" /></div>;
  }

  const isReadOnly = activeRoom?.nombre === 'Empresas' && !user?.firstRole;

  const readOnlyNotice = { icon: <FaBriefcase />, text: 'Este canal es de solo lectura. El equipo publica las ofertas y novedades.' };

  return (
    <div className="chat-page">
      {/* Main */}
      <div className="chat-main">
        {/* Room header */}
        <div className="chat-header">
          <div className="chat-header-icon">
            {ICON_MAP[activeRoom?.icono] || <FaHashtag />}
          </div>
          <div className="chat-header-info">
            <h2>{activeRoom?.nombre}</h2>
            <span>{activeRoom?.descripcion || 'Canal de la comunidad'}</span>
          </div>
        </div>

        {/* Mobile channel tabs */}
        <div className="chat-mobile-tabs">
          {canales.map(canal => (
            <button
              key={canal.id}
              className={`chat-mobile-tab ${activeRoom?.id === canal.id ? 'active' : ''}`}
              onClick={() => handleRoomClick(canal)}
            >
              <span className="chat-mobile-tab-icon">{ICON_MAP[canal.icono] || <FaHashtag />}</span>
              <span>{canal.nombre}</span>
            </button>
          ))}
        </div>

        {/* Pinned message banner */}
        {pinnedMsg && (
          <div className="chat-pinned-banner">
            <FaThumbtack className="chat-pinned-icon" />
            <div className="chat-pinned-content">
              <span className="chat-pinned-label">Mensaje fijado</span>
              <span className="chat-pinned-text" dangerouslySetInnerHTML={{ __html: pinnedMsg.body }} />
            </div>
            {user?.firstRole && (
              <button className="chat-pinned-unpin" title="Desfijar" onClick={() => handlePin(pinnedMsg)}>
                ×
              </button>
            )}
          </div>
        )}

        {/* Feed */}
        <div className="chat-feed" ref={feedRef}>
          {/* Load more older messages */}
          {hasMoreMsgs && (
            <button className="chat-load-older" onClick={loadMoreMessages} disabled={loadingMore}>
              <FaChevronUp />
              {loadingMore ? 'Cargando…' : 'Ver mensajes anteriores'}
            </button>
          )}

          {posts.length === 0 && !hasMoreMsgs && (
            <p className="chat-empty">Sé el primero en escribir en este canal.</p>
          )}

          {posts.map(post => (
            <article key={post.id} className="chat-post">
              <div className="chat-post-header">
                <Avatar username={post.username || post.author} avatarUrl={post.avatar_url} size="md" />
                <div className="chat-post-meta">
                  <div className="chat-post-author-row">
                    <span className="chat-post-author">{post.username || post.author}</span>
                    {post.user_tag && <span className="chat-role-badge">{post.user_tag}</span>}
                  </div>
                  <div className="chat-post-time">{timeSince(post.created_at || post.time)}</div>
                </div>
                {post.tag && <span className="chat-post-tag">{post.tag}</span>}
                {user?.firstRole && (
                  <button
                    className={`chat-pin-btn ${pinnedMsg?.id === post.id ? 'chat-pin-btn--active' : ''}`}
                    title={pinnedMsg?.id === post.id ? 'Desfijar' : 'Fijar mensaje'}
                    onClick={() => handlePin(post)}
                  >
                    <FaThumbtack />
                  </button>
                )}
                {(post.id_usuario === user?.id || user?.firstRole) && (
                  <button className="chat-delete-btn" title="Eliminar" onClick={() => handleDelete(post.id)}>
                    <FaTrash />
                  </button>
                )}
              </div>

              <div className="chat-post-body" dangerouslySetInnerHTML={{ __html: post.body }} />

              <div className="chat-post-actions">
                <button
                  className={`chat-action-btn ${post.liked ? 'liked' : ''}`}
                  onClick={() => handleLike(post.id, post.liked)}
                >
                  {post.liked ? <FaHeart /> : <FaRegHeart />}
                  {post.likes}
                </button>
                <button
                  className={`chat-action-btn ${openComments[post.id] ? 'open' : ''}`}
                  onClick={() => toggleComments(post.id)}
                >
                  <FaRegComment />
                  {post.comments || 0}
                </button>
              </div>

              {openComments[post.id] && (
                <div className="chat-replies">
                  {(replies[post.id] || []).map(reply => (
                    <div key={reply.id} className="chat-reply">
                      <Avatar username={reply.username} avatarUrl={reply.avatar_url} size="sm" />
                      <div className="chat-reply-body">
                        <div className="chat-reply-meta">
                          <span className="chat-post-author">{reply.username}</span>
                          {reply.user_tag && <span className="chat-role-badge chat-role-badge--sm">{reply.user_tag}</span>}
                          <span className="chat-post-time">{timeSince(reply.created_at)}</span>
                          {reply.id_usuario === user?.id && (
                            <button className="chat-delete-btn chat-delete-btn--sm" title="Eliminar" onClick={() => handleDeleteReply(post.id, reply.id)}>
                              <FaTrash />
                            </button>
                          )}
                        </div>
                        <div className="chat-post-body" dangerouslySetInnerHTML={{ __html: reply.body }} />
                      </div>
                    </div>
                  ))}
                  {replies[post.id]?.length === 0 && (
                    <p className="chat-replies-empty">Sé el primero en comentar.</p>
                  )}
                  {repliesHasMore[post.id] && (
                    <button className="chat-load-more" onClick={() => loadMoreReplies(post.id)}>
                      Cargar más comentarios
                    </button>
                  )}
                  <div className="chat-reply-input">
                    <Avatar username={user?.username} avatarUrl={user?.avatarUrl} size="sm" />
                    <input
                      className="chat-reply-field"
                      placeholder="Escribe un comentario..."
                      value={replyInputs[post.id] || ''}
                      onChange={e => setReplyInputs(prev => ({ ...prev, [post.id]: e.target.value }))}
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); sendReply(post.id); } }}
                    />
                    <button className="chat-send-btn" onClick={() => sendReply(post.id)}>
                      <FaPaperPlane />
                    </button>
                  </div>
                </div>
              )}
            </article>
          ))}
        </div>

        {/* Input */}
        {isReadOnly ? (
          <div className="chat-readonly-notice">
            {readOnlyNotice.icon}
            <span>{readOnlyNotice.text}</span>
          </div>
        ) : (
          <div className="chat-input-area">
            <div className="chat-input-wrap">
              <Avatar username={user?.username} avatarUrl={user?.avatarUrl} size="sm" />
              <textarea
                ref={textareaRef}
                className="chat-input"
                placeholder={`Escribe en #${activeRoom?.nombre || ''}...`}
                value={inputValue}
                onChange={e => setInputValue(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
                }}
              />
              <button className="chat-send-btn" onClick={handleSend}>
                <FaPaperPlane />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Right sidebar — desktop only */}
      <aside className="chat-sidebar">
        <div className="chat-sidebar-title">Canales</div>
        {canales.map(canal => (
          <button
            key={canal.id}
            className={`chat-room-btn ${activeRoom?.id === canal.id ? 'active' : ''}`}
            onClick={() => handleRoomClick(canal)}
          >
            <span className="chat-room-icon">{ICON_MAP[canal.icono] || <FaHashtag />}</span>
            <span>{canal.nombre}</span>
          </button>
        ))}
      </aside>
    </div>
  );
};

export default Chat;
